<script setup>
// Addendum §4.2 Select-all scope — the highest-risk interaction in the product.
// The header checkbox selects THE CURRENT PAGE only. When the whole page is
// selected, this banner offers to widen to the entire matching query. Choosing
// it sets `selectAllMatching` (a flag + the active filter set), NOT a
// materialised list of IDs — rows arriving or changing before commit resolve
// server-side against the filter. Any filter change must clear the selection
// (host responsibility). The commit confirmation names the true scope.
import { computed } from 'vue'

const props = defineProps({
	pageSelectedCount: { type: Number, default: 0 },
	pageTotal: { type: Number, default: 0 },
	matchingTotal: { type: Number, default: 0 },
	selectAllMatching: { type: Boolean, default: false },
	noun: { type: String, default: 'row' },
})
const emit = defineEmits(['select-all-matching', 'clear'])

const pageFull = computed(() => props.pageTotal > 0 && props.pageSelectedCount === props.pageTotal)
const show = computed(() => props.selectAllMatching || (pageFull.value && props.matchingTotal > props.pageTotal))
const plural = (n) => (n === 1 ? props.noun : props.noun + 's')
</script>

<template>
	<div v-if="show" class="l-selectall" role="status">
		<template v-if="selectAllMatching">
			<span class="msg">
				All <span class="num n">{{ matchingTotal }}</span> matching {{ plural(matchingTotal) }} are selected.
			</span>
			<button type="button" class="link" @click="emit('clear')">Clear selection</button>
		</template>
		<template v-else>
			<span class="msg">
				All <span class="num n">{{ pageTotal }}</span> on this page are selected.
			</span>
			<button type="button" class="link strong" @click="emit('select-all-matching')">
				Select all {{ matchingTotal }} matching
			</button>
		</template>
	</div>
</template>

<style scoped>
.l-selectall
{
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--sp-2);
	flex-wrap: wrap;
	padding: 9px var(--sp-4);
	background: var(--interactive-tint);
	border: 1px solid color-mix(in srgb, var(--interactive) 22%, transparent);
	border-radius: var(--r-sm);
	font-size: var(--text-sm);
	color: var(--text-2);
}

.msg .n
{
	font-weight: 600;
	color: var(--text);
}

.link
{
	font-family: var(--font-ui);
	font-size: var(--text-sm);
	font-weight: 600;
	color: var(--interactive-text);
	background: none;
	border: 0;
	cursor: pointer;
	text-decoration: underline;
	text-underline-offset: 2px;
	min-height: var(--target-min);
	padding: 0 4px;
}

.link:hover
{
	color: var(--interactive-hover);
}
</style>
